import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MedicationSheetForm from "../MedicationSheet/MedicationSheetForm";
import { createMedicationSheet, clearError } from "../../../store/slices/medicationSheetsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddMedicationSheet = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { isLoading, error } = useSelector((state) => state.medicationSheets);

  const [formData, setFormData] = useState({
    patientId,
    caseId,
    name: "",
    bedNo: "",
    doa: "",
    mrNo: "",
    age: "",
    consultant: "",
    ward: "",
    diagnosis: "",
    documentName: "Medication Sheet",
    effectiveDate: "",
    approvedBy: "",
    approvedDate: "",
    nextRevisionBy: "",
    nextRevisionDate: "",
    medications: [
      {
        id: Date.now(),
        drugName: "",
        sign: "",
        startDate: "",
        timeEntries: [], // Changed from Array(8).fill(false) to empty array
      },
    ],
  });

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => {
          if (patient) {
            const calculatedAge = (dobString) => {
              if (!dobString) return "";
              const today = new Date();
              const birthDate = new Date(dobString);
              let age = today.getFullYear() - birthDate.getFullYear();
              const m = today.getMonth() - birthDate.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              return age;
            };

            setFormData((prev) => ({
              ...prev,
              name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
              age: calculatedAge(patient.dob),
            }));
          }
        })
        .catch((err) => console.error("Failed to fetch patient:", err));
    }
  }, [dispatch, patientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for required fields
    const required = ["name", "bedNo", "doa"];
    const missing = required.filter((f) => !formData[f]);
    if (missing.length > 0) {
      Swal.fire({
        title: "Missing Fields",
        text: `Please fill: ${missing.join(", ")}`,
        icon: "warning",
      });
      return;
    }

    // Validate medications
    if (formData.medications.some((m) => !m.drugName)) {
      Swal.fire({
        title: "Validation Error",
        text: "All medication rows must have a Drug Name.",
        icon: "warning",
      });
      return;
    }

    // Validate time entries for each medication
    const medicationsWithEmptyTimeEntries = formData.medications.filter(
      (med) => med.timeEntries.length === 0
    );

    if (medicationsWithEmptyTimeEntries.length > 0) {
      Swal.fire({
        title: "Missing Time Entries",
        text: "Each medication must have at least one time entry.",
        icon: "warning",
      });
      return;
    }

    // Helper: empty string → null
    const toNull = (val) => (val === "" ? null : val);

    // Format date to YYYY-MM-DD format for backend
    const formatDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    // Format datetime for time entries
    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return null;
      // Ensure the format is correct for backend
      return dateTimeString.replace('T', ' ') + ':00';
    };

    // Final payload with snake_case
    const payload = {
      // Master fields
      patient_id: toNull(formData.patientId),
      case_id: toNull(formData.caseId),
      name: formData.name,
      bed_no: toNull(formData.bedNo),
      doa: formatDate(formData.doa),
      mr_no: toNull(formData.mrNo),
      age: toNull(formData.age),
      consultant: toNull(formData.consultant),
      ward: toNull(formData.ward),
      diagnosis: toNull(formData.diagnosis),
      document_name: formData.documentName,
      effective_date: formatDate(formData.effectiveDate),
      approved_by: toNull(formData.approvedBy),
      approved_date: formatDate(formData.approvedDate),
      next_revision_by: toNull(formData.nextRevisionBy),
      next_revision_date: formatDate(formData.nextRevisionDate),

      // Child rows (medications with their time entries)
      medications: formData.medications.map((m) => ({
        drugName: m.drugName,
        sign: toNull(m.sign),
        startDate: formatDate(m.startDate),
        timeEntries: m.timeEntries.map((timeEntry) => ({
          given: timeEntry.given || null,
          time_date: formatDateTime(timeEntry.time_date),
        })),
      })),
    };

    console.log("Submitting payload:", JSON.stringify(payload, null, 2));

    try {
      await dispatch(createMedicationSheet(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Medication sheet added successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate(`/cases/${payload.case_id}/profile`, {
        state: {
          patientId: payload.patient_id,
          caseId: payload.case_id
        }
      });
    } catch (err) {
      console.error("Error submitting form:", err);

      const errorMsg = err?.errors
        ? Object.values(err.errors).flat().join("\n")
        : err?.message || err || "Failed to create medication sheet.";

      Swal.fire({
        title: "Error",
        text: errorMsg,
        icon: "error",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card">
          <div className="card-body">
            {/* Backend Error */}
            {error && (
              <div className="alert alert-danger alert-dismissible">
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(clearError())}
                />
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <MedicationSheetForm
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                isLoading={isLoading}
              />
              <div className="col-12 d-flex justify-content-end mt-4">
                <div className="col-md-2 d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-2-line me-1"></i> Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddMedicationSheet;